import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Loading from '../../../components/loading';
import ProcedureCatalogFormModal from '../../../register/procedures/procedureCatalogFormModal';
import { useProceduresCatalog } from '../../../hooks/useProceduresCatalog';
import { useSaveAestheticProcedure } from './hooks/useSaveAestheticProcedure';

export default function AestheticalProcedureModal({ open, onClose, baseUrl, procedure, onSave }) {
  const navigate = useNavigate();

  const isEditing = Boolean(procedure);

  const { proceduresCatalog: catalog, refetch: fetchCatalog } = useProceduresCatalog();
  const { save: saveProcedureRequest } = useSaveAestheticProcedure(baseUrl);

  const [isLoading, setLoading] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [autocompleteInputValue, setAutocompleteInputValue] = useState('');
  const [addCatalogInitialDescription, setAddCatalogInitialDescription] = useState('');
  const [isAddCatalogOpen, setAddCatalogOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [type, setType] = useState('INJECTABLE_APPLICATION');
  const [observation, setObservation] = useState('');
  const [date, setDate] = useState(dayjs());
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      fetchCatalog();
      if (procedure) {
        setAmount(
          procedure.amount != null
            ? String(procedure.amount)
                .replace(/R\$\s*/i, '')
                .trim()
            : '',
        );
        setType(procedure.type ?? 'INJECTABLE_APPLICATION');
        setObservation(procedure.observation ?? '');
        setDate(procedure.executionDate ? dayjs(procedure.executionDate, 'DD/MM/YYYY') : null);
        setAutocompleteInputValue(procedure.procedureDescription ?? '');
      }
    } else {
      setSelectedCatalog(null);
      setAutocompleteInputValue('');
      setAmount('');
      setType('INJECTABLE_APPLICATION');
      setObservation('');
      setDate(dayjs());
      setError('');
    }
  }, [open, procedure, fetchCatalog]);

  useEffect(() => {
    if (open && procedure && catalog.length) {
      const match = catalog.find(
        (item) =>
          item.id === procedure.procedureCatalogId ||
          item.description === procedure.procedureDescription,
      );
      if (match) setSelectedCatalog(match);
    }
  }, [open, procedure, catalog]);

  const validate = () => {
    if (!selectedCatalog || !amount) {
      setError('É obrigatório informar: Procedimento e Valor para cadastrar um procedimento');
      return false;
    }
    setError('');
    return true;
  };

  const saveProcedure = (event) => {
    event.preventDefault();
    if (!validate()) return;

    const payload = {
      procedureCatalogId: selectedCatalog.id,
      amount,
      type,
      observation,
      executionDate: date ? dayjs(date).format('DD/MM/YYYY') : null,
    };
    setLoading(true);
    saveProcedureRequest(isEditing ? procedure.id : null, payload)
      .then((returnFromApi) => {
        onSave(returnFromApi.data);
      })
      .catch((returnFromApi) => {
        const responseData = returnFromApi.response?.data;
        setError(
          typeof responseData === 'string'
            ? responseData
            : 'Não foi possível salvar o procedimento.',
        );
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <Loading isToHidden={isLoading === false} />

        <DialogTitle>{isEditing ? 'Editar Procedimento' : 'Adicionar Procedimento'}</DialogTitle>
        <form id="aesthetical-procedure-form" onSubmit={saveProcedure}>
          <DialogContent>
            <Collapse in={!!error}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            </Collapse>
            <Grid container spacing={0.2}>
              <Grid size={12}>
                <FormControl fullWidth>
                  <FormLabel>Procedimento:</FormLabel>
                  <Autocomplete
                    options={catalog}
                    getOptionLabel={(option) => option.description ?? ''}
                    value={selectedCatalog}
                    inputValue={autocompleteInputValue}
                    onInputChange={(_, newInputValue) => setAutocompleteInputValue(newInputValue)}
                    onChange={(_, newValue) => {
                      setSelectedCatalog(newValue);
                      if (newValue?.amount != null) setAmount(String(newValue.amount));
                    }}
                    noOptionsText="Nenhum procedimento encontrado."
                    PaperComponent={({ children, ...other }) => (
                      <Paper {...other}>
                        {children}
                        <Box
                          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <Button
                            fullWidth
                            size="small"
                            onClick={() => {
                              setAddCatalogInitialDescription(autocompleteInputValue);
                              setAddCatalogOpen(true);
                            }}
                          >
                            + Adicionar procedimento
                          </Button>
                        </Box>
                      </Paper>
                    )}
                    renderInput={(params) => <TextField {...params} fullWidth variant="outlined" />}
                  />
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth>
                  <FormLabel>Valor:</FormLabel>
                  <TextField
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    fullWidth
                    inputProps={{ inputMode: 'decimal' }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    }}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth>
                  <FormLabel>Data de realização:</FormLabel>
                  <DatePicker
                    value={date}
                    onChange={(newDate) => setDate(newDate)}
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { fullWidth: true, variant: 'outlined' } }}
                  />
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <FormLabel>Tipo:</FormLabel>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={type === 'INJECTABLE_APPLICATION'}
                          onChange={() => setType('INJECTABLE_APPLICATION')}
                        />
                      }
                      label={
                        <Box component="span">
                          Aplicação Injetável{' '}
                          <Typography component="span" variant="caption" color="text.secondary">
                            (Mapa)
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={type === 'PARAMETER'}
                          onChange={() => setType('PARAMETER')}
                        />
                      }
                      label="Parâmetro"
                    />
                  </FormGroup>
                </FormControl>
              </Grid>

              <Grid size={12}>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <FormLabel>Observação:</FormLabel>
                  <TextField
                    value={observation}
                    onChange={(e) => setObservation(e.target.value)}
                    fullWidth
                    multiline
                    minRows={2}
                    variant="outlined"
                  />
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button variant="outlined" onClick={onClose}>
              Cancelar
            </Button>
            <Button variant="contained" type="submit" form="aesthetical-procedure-form">
              Salvar
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ProcedureCatalogFormModal
        open={isAddCatalogOpen}
        onClose={() => setAddCatalogOpen(false)}
        onSave={fetchCatalog}
        initialDescription={addCatalogInitialDescription}
      />
    </>
  );
}

AestheticalProcedureModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  baseUrl: PropTypes.string,
  procedure: PropTypes.shape({
    id: PropTypes.number,
    procedureCatalogId: PropTypes.number,
    procedureDescription: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    type: PropTypes.string,
  }),
  onSave: PropTypes.func.isRequired,
};

AestheticalProcedureModal.defaultProps = {
  baseUrl: null,
  procedure: null,
};
